import { memo, useLayoutEffect, useRef, type ReactNode } from "react";
import { Message, MessageContent } from "@/components/ui/message";
import { Response } from "@/components/ui/response";
import { ShimmeringText } from "@/components/ui/shimmering-text";
import { cn } from "@/components/ui/utils";
import type { VoiceTurn } from "@/lib/voice/conversation";

interface VoiceTranscriptProps {
  className?: string;
  turns: VoiceTurn[];
  renderActions?: (turn: VoiceTurn) => ReactNode;
  displayText?: (turn: VoiceTurn) => string;
}

const FOLLOW_THRESHOLD_PX = 32;

const ROLE_LABEL = {
  assistant: "Assistant",
  operator: "You",
} as const;

function TranscriptTurn({
  renderActions,
  displayText,
  turn,
}: {
  renderActions?: (turn: VoiceTurn) => ReactNode;
  displayText?: (turn: VoiceTurn) => string;
  turn: VoiceTurn;
}) {
  const isAssistant = turn.role === "assistant";
  const text = isAssistant && displayText ? displayText(turn) : turn.text;

  return (
    <li>
      <Message from={turn.role === "operator" ? "user" : "assistant"}>
        <MessageContent variant="contained">
          <p
            className={cn(
              "font-medium text-tag-sm uppercase tracking-wide",
              isAssistant && "opacity-70",
            )}
          >
            {ROLE_LABEL[turn.role]}
          </p>
          {isAssistant ? (
            turn.isFinal ? (
              <Response className="text-sm">{text}</Response>
            ) : turn.isIncomplete ? (
              <p className="whitespace-pre-wrap break-words text-sm">{text}</p>
            ) : (
              <div className="italic opacity-90 text-sm">
                <ShimmeringText text={text} />
              </div>
            )
          ) : (
            <p
              className={cn(
                "whitespace-pre-wrap break-words",
                !turn.isFinal && "italic opacity-80",
              )}
            >
              {turn.text}
            </p>
          )}
          {renderActions?.(turn)}
          {turn.isPlaybackIncomplete && (
            <p className="text-xs opacity-75">
              Audio cut off. The connection ended while the assistant was speaking.
            </p>
          )}
        </MessageContent>
      </Message>
    </li>
  );
}

/**
 * Rolling transcript of the current voice session, newest turn last.
 *
 * Memoised because status frames (listening ⇄ speaking) re-render the page far
 * more often than new turns arrive, and the turns array keeps its identity
 * across them.
 */
export const VoiceTranscript = memo(function VoiceTranscript({
  className,
  turns,
  renderActions,
  displayText,
}: VoiceTranscriptProps) {
  const regionRef = useRef<HTMLElement>(null);
  const followsLatestRef = useRef(true);

  useLayoutEffect(() => {
    const region = regionRef.current;
    if (region && followsLatestRef.current) {
      region.scrollTop = region.scrollHeight;
    }
  }, [turns]);

  const trackScrollPosition = () => {
    const region = regionRef.current;
    if (!region) {
      return;
    }
    const distanceFromBottom = region.scrollHeight - region.scrollTop - region.clientHeight;
    followsLatestRef.current = distanceFromBottom <= FOLLOW_THRESHOLD_PX;
  };

  if (turns.length === 0) {
    return (
      <p className="text-center text-body text-muted-foreground">
        The transcript appears here once the conversation starts.
      </p>
    );
  }

  return (
    <section
      aria-label="Conversation transcript"
      aria-live="polite"
      aria-relevant="additions text"
      // A long call would otherwise push the orb off-screen; capping the
      // height keeps the control reachable without scrolling the page.
      className={cn("max-h-[50dvh] overflow-y-auto", className)}
      onScroll={trackScrollPosition}
      ref={regionRef}
      // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- WAI-ARIA APG requires a scrollable region to be keyboard focusable so overflowed turns stay reachable
      tabIndex={0}
    >
      <ul className="flex flex-col divide-y divide-border/60">
        {turns.map((turn) => (
          <TranscriptTurn
            key={turn.id}
            renderActions={renderActions}
            displayText={displayText}
            turn={turn}
          />
        ))}
      </ul>
    </section>
  );
});
